import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const Profile = () => {
  const { user, updateProfile, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  
  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await updateProfile(formData);
    
    if (result.success) {
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  const handleCancel = () => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || ''
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Password change handlers
  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    setPasswordError('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    try {
      await axios.put('/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      setShowPasswordModal(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setSuccess('Password changed successfully!');
    } catch (error) {
      setPasswordError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Download data handler
  const handleDownloadData = async () => {
    try {
      const response = await axios.get('/export-user-data', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user-data-${user?.username}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('User data exported successfully!');
    } catch (error) {
      setError('Failed to export user data');
    }
  };

  // Delete account handlers
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      setError('Please type "DELETE" to confirm account deletion');
      return;
    }

    setDeleteLoading(true);
    try {
      await axios.delete('/delete-account');
      logout();
      // Redirect will happen automatically due to logout
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete account');
      setDeleteLoading(false);
    }
  };

  return (
    <div className="main-content">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Profile</h1>
        <p className="text-gray-600">Manage your account information</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div className="flex justify-between items-center">
                <h2 className="card-title">Personal Information</h2>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="btn btn-outline btn-sm"
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger mb-4">
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success mb-4">
                  {success}
                </div>
              )}

              {isEditing ? (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label" htmlFor="first_name">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        className="form-control"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="last_name">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        className="form-control"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="email">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className="spinner"></div>
                          Updating...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="btn btn-outline"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">First Name</label>
                      <div className="py-2 text-gray-800">{user?.first_name}</div>
                    </div>
                    <div>
                      <label className="form-label">Last Name</label>
                      <div className="py-2 text-gray-800">{user?.last_name}</div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="form-label">Email Address</label>
                    <div className="py-2 text-gray-800">{user?.email}</div>
                  </div>

                  <div>
                    <label className="form-label">Username</label>
                    <div className="py-2 text-gray-800">{user?.username}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Account Details */}
        <div>
          <div className="card mb-6">
            <div className="card-header">
              <h3 className="card-title">Account Details</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">Role:</span>
                  <span className="user-role">{user?.role}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Status:</span>
                  <span className={user?.is_active ? 'text-green-600' : 'text-red-600'}>
                    {user?.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Member since:</span>
                  <span>{formatDate(user?.created_at)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Account Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Account Actions</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <button 
                  onClick={() => setShowPasswordModal(true)}
                  className="btn btn-outline w-full"
                >
                  Change Password
                </button>
                <button 
                  onClick={handleDownloadData}
                  className="btn btn-outline w-full"
                >
                  Download Data
                </button>
                <div className="border-t pt-3">
                  <button 
                    onClick={() => setShowDeleteModal(true)}
                    className="btn btn-danger w-full"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Summary */}
      <div className="card mt-6">
        <div className="card-header">
          <h3 className="card-title">Activity Summary</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-primary-color">--</div>
              <div className="text-sm text-gray-600">Issues Created</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary-color">--</div>
              <div className="text-sm text-gray-600">Issues Assigned</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary-color">--</div>
              <div className="text-sm text-gray-600">Comments Posted</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary-color">--</div>
              <div className="text-sm text-gray-600">Issues Resolved</div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Change Password</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="btn btn-outline btn-sm"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handlePasswordSubmit}>
              <div className="modal-body">
                {passwordError && (
                  <div className="alert alert-danger mb-4">
                    {passwordError}
                  </div>
                )}
                
                <div className="form-group">
                  <label className="form-label" htmlFor="current_password">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="current_password"
                    name="current_password"
                    className="form-control"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="new_password">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="new_password"
                    name="new_password"
                    className="form-control"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="confirm_password">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirm_password"
                    name="confirm_password"
                    className="form-control"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? (
                    <>
                      <div className="spinner"></div>
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Delete Account</h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-outline btn-sm"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="alert alert-danger mb-4">
                <strong>Warning:</strong> This action cannot be undone. All your data will be permanently deleted.
              </div>
              
              <p className="mb-4">
                Type <strong>DELETE</strong> to confirm you want to permanently delete your account:
              </p>
              
              <div className="form-group">
                <input
                  type="text"
                  className="form-control"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Type DELETE to confirm"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="btn btn-danger"
                disabled={deleteLoading || deleteConfirmation !== 'DELETE'}
              >
                {deleteLoading ? (
                  <>
                    <div className="spinner"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 